import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/cs';
import { createRoot } from 'react-dom/client';
import { Uppy } from '@uppy/core';
import Tus from '@uppy/tus';
import BeeyApp, { useSession, Localization, initLongPolling } from '@newtontechnologies/applib-frontend';
import en from './resources/translations/en-US.json';
import pl from './resources/translations/pl-PL.json';
import cs from './resources/translations/cs-CZ.json';
import FileSplitterForm from './components/FileSplitterForm';
import TasksList from './components/TasksList';
import { UploadContext } from './upload-context';
import 'regenerator-runtime/runtime';
import '@newtontechnologies/bapp-lib-frontend/dist/style.css';
import { version } from '../package.json';
const formatTime = (minutes) => {
    const hrs = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mins = String(minutes % 60).padStart(2, '0');
    return `${hrs}:${mins}:00`;
};
const siteOrigin = window.location.origin;
const App = () => {
    const { connection } = useSession();
    const [processing, setProcessing] = useState(null);
    const [uploadDisabled, setUploadDisabled] = useState(false);
    const [uploadStatus, setUploadStatus] = useState({
        uploadFinished: false,
        uploadProgress: 0,
    });
    const fetchMediaFiles = async (queue) => {
        const response = await connection.authFetch()
            .url(`${siteOrigin}/apps/splitter/API/MediaFile/List?Skip=0&Take=50`)
            .fetch();
        // NOTE: The seperate json call won't be necessary as soon as the app API endpoints
        // return the same structure as Beey endpoints
        const list = await response.json();
        setProcessing({
            queue,
            tasks: list.items,
        });
    };
    const uppy = new Uppy({
        meta: { type: 'file' },
        restrictions: {
            allowedFileTypes: ['audio/*', 'video/*'],
        },
    }).use(Tus, {
        endpoint: 'files/',
        retryDelays: [0, 3000, 7000, 10000, 12000, 14000, 15000, 17000, 18000, 19000],
        removeFingerprintOnSuccess: true,
        chunkSize: 8 * 1024 * 1024,
        async onBeforeRequest(req) {
            req.setHeader('Authorization', await connection.retrieveAuthString());
        },
    })
        .on('progress', (progress) => {
        if (progress === 0) {
            window.onbeforeunload = null;
        }
        else {
            window.onbeforeunload = () => true;
        }
        setUploadDisabled(true);
        setUploadStatus(Object.assign(Object.assign({}, uploadStatus), { uploadProgress: progress }));
    })
        .on('complete', () => {
        uppy.cancelAll();
        setUploadDisabled(false);
        setUploadStatus(Object.assign(Object.assign({}, uploadStatus), { uploadFinished: true }));
    })
        .on('cancel-all', () => setUploadDisabled(false))
        .on('upload-error', (file, error) => {
        if (error.isNetworkError === true && file !== undefined) {
            void uppy.retryUpload(file.id);
        }
    });
    useEffect(() => {
        const [run, stop] = initLongPolling({
            pollUrl: `${siteOrigin}/apps/splitter/API/Queue`,
            onPoll: fetchMediaFiles,
            retrieveAuthToken: connection.retrieveAuthString,
        });
        run();
        return stop;
    }, []);
    const handleUploadFile = ({ title, minutes, mediaFile, language, withVAD, }) => {
        uppy.addFile({
            name: mediaFile.name,
            type: 'audio/*, video/*',
            data: mediaFile,
            meta: {
                splitLength: formatTime(minutes),
                language,
                projectname: title,
                withVAD,
            },
        });
        void uppy.upload();
    };
    const handleDeleteTask = async (fileId) => {
        await connection.authFetch()
            .url(`${siteOrigin}/apps/splitter/API/MediaFile?mediaFileId=${fileId}`)
            .delete()
            .fetch();
        uppy.cancelAll();
    };
    return (_jsxs(UploadContext.Provider, { value: uploadStatus, children: [_jsx(FileSplitterForm, { onUploadFile: (file) => handleUploadFile(file), uploadDisabled: uploadDisabled }), processing === null ? null : (_jsx(TasksList, { tasks: processing.tasks, onDeleteTask: handleDeleteTask, queue: processing.queue }))] }));
};
const localization = new Localization({
    'en-US': en, 'pl-PL': pl, 'cs-CZ': cs,
}, 'en-US');
createRoot(document.querySelector('#app')).render(_jsx(BeeyApp, { name: "Splitter", version: version, localization: localization, children: _jsx(App, {}) }));
